import React from 'react';
import { Link } from 'react-router-dom';
import { STORIES } from '../data/storiesData';

export default function Stories() {
  const featured = STORIES.slice(0, 5);
  const founderStory = STORIES.find((story) => story.slug === 'founder');

  return (
    <div className="stories-page">
      <section className="stories-hero">
        <div className="stories-hero-bg" style={{ backgroundImage: `url('${founderStory?.heroImage || featured[0].heroImage}')` }}></div>
        <div className="stories-hero-overlay"></div>
        <div className="stories-hero-content">
          <span className="stories-kicker">Editorial Stories</span>
          <h1>Built in 2026. Rooted in Family Craft Since 1965.</h1>
          <p>
            The stories section is about people, process, and origin. Start with founder notes from Maaz Ajaz,
            then explore the places and craft disciplines that shape every Kaaleen Haus rug.
          </p>
          <div className="stories-hero-actions">
            <Link to="/stories/founder" className="stories-btn-primary">Read Founder Story</Link>
            <Link to="/contact#contact-form" className="stories-btn-secondary">Book a Consultation</Link>
          </div>
        </div>
      </section>

      <section className="stories-founder-band">
        <div className="stories-founder-band-content">
          <span className="stories-kicker">Founder Spotlight</span>
          <h2>Maaz Ajaz</h2>
          <p>
            Kaaleen Haus was established in 2026 by Maaz Ajaz, continuing a family business legacy that began in 1965.
            This section is intentionally personal, and your founder portrait can be shown in the dedicated Founder story page.
          </p>
          <Link to="/stories/founder" className="stories-inline-link">Open Founder Page</Link>
        </div>
      </section>

      <section className="stories-featured" id="stories-grid">
        <div className="stories-headline-row">
          <h2>Featured Stories</h2>
          <p>Hover on Stories in the navbar anytime to jump straight to these pages.</p>
        </div>

        <div className="stories-card-grid">
          {featured.map((story, index) => (
            <Link key={story.slug} className="stories-card" to={`/stories/${story.slug}`}>
              <div className="stories-card-image" style={{ backgroundImage: `url('${story.cardImage}')` }}></div>
              <div className="stories-card-meta">
                <span className="stories-card-index">0{index + 1}</span>
                <h3>{story.navLabel}</h3>
                <p>{story.navSub}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="stories-layout-section">
        <div className="stories-layout-grid">
          <article>
            <span className="stories-kicker">What You Will Find</span>
            <h2>A Richer Reading Experience</h2>
            <p>
              Each story has a visual hero, milestone stats, deep-dive narrative blocks, founder or studio voice,
              and related stories at the end for natural discovery.
            </p>
            <ul>
              <li>Founder timeline: 1965 family roots to 2026 company launch</li>
              <li>Bhadohi city narratives that shape visual direction</li>
              <li>Craft process stories from material prep to final finish</li>
              <li>Design thinking and quality discipline behind each collection</li>
            </ul>
          </article>
          <div className="stories-layout-image" style={{ backgroundImage: `url('${featured[3].heroImage}')` }}></div>
        </div>
      </section>

      <section className="stories-timeline-section">
        <span className="stories-kicker">Brand Timeline</span>
        <h2>From Legacy Experience to New-Generation Brand</h2>
        <div className="stories-timeline">
          {[
            { year: '1965', title: 'Family Journey Begins', copy: 'The family enters the rug and textile craft ecosystem.' },
            { year: '2000+', title: 'Expanded Know-How', copy: 'Decades of sourcing, weaving, and quality process experience accumulate.' },
            { year: '2026', title: 'Kaaleen Haus Founded', copy: 'Maaz Ajaz launches a focused premium rug brand with modern positioning.' },
            { year: 'Now', title: 'Founder-Led Execution', copy: 'Every collection is shaped with direct founder oversight and quality focus.' },
          ].map((step) => (
            <div key={step.year} className="stories-timeline-card">
              <span>{step.year}</span>
              <h3>{step.title}</h3>
              <p>{step.copy}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="stories-bottom-cta">
        <h2>Explore the Full Story Universe</h2>
        <p>Start with the founder chapter, then continue into city, craft, and design narratives.</p>
        <div className="stories-bottom-actions">
          <Link to="/stories/founder" className="stories-btn-primary">Start with Founder</Link>
          <Link to="/shop" className="stories-btn-secondary">Explore Rugs</Link>
        </div>
      </section>

      <style>{storiesStyles}</style>
    </div>
  );
}

const storiesStyles = `
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@300;400;500;600;700&family=Playfair+Display:wght@400;500;600;700&display=swap');

.stories-page {
  background:
    radial-gradient(circle at 12% 7%, rgba(188, 145, 72, 0.14) 0%, rgba(188, 145, 72, 0) 32%),
    linear-gradient(180deg, #f4f2ec 0%, #f9f7f2 100%);
  color: #1b1d30;
  font-family: 'Manrope', sans-serif;
}

.stories-kicker {
  display: inline-block;
  margin-bottom: 12px;
  font-size: 11px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #b3843d;
  font-weight: 700;
}

.stories-hero {
  min-height: 74vh;
  position: relative;
  display: flex;
  align-items: center;
  overflow: hidden;
}

.stories-hero-bg {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  transform: scale(1.04);
}

.stories-hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(160deg, rgba(8, 12, 24, 0.68) 0%, rgba(8, 12, 24, 0.82) 55%, rgba(8, 12, 24, 0.72) 100%);
}

.stories-hero-content {
  position: relative;
  z-index: 1;
  max-width: 1160px;
  width: 100%;
  margin: 0 auto;
  padding: 0 24px;
}

.stories-hero-content h1 {
  margin: 0;
  max-width: 760px;
  color: #ffffff;
  font-family: 'Playfair Display', serif;
  font-size: clamp(2rem, 4.9vw, 3.8rem);
  line-height: 1.05;
}

.stories-hero-content p {
  margin: 14px 0 0;
  max-width: 700px;
  color: rgba(255, 255, 255, 0.86);
  line-height: 1.75;
}

.stories-hero-actions {
  margin-top: 22px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.stories-btn-primary,
.stories-btn-secondary {
  display: inline-block;
  padding: 12px 20px;
  border-radius: 999px;
  text-decoration: none;
  font-size: 11px;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  font-weight: 700;
}

.stories-btn-primary {
  background: linear-gradient(120deg, #d4b272, #a77934);
  color: #121529;
}

.stories-btn-secondary {
  border: 1px solid rgba(255, 255, 255, 0.42);
  color: #f8f3ea;
  background: rgba(255, 255, 255, 0.08);
}

.stories-founder-band,
.stories-featured,
.stories-layout-section,
.stories-timeline-section,
.stories-bottom-cta {
  max-width: 1240px;
  margin: 0 auto;
  padding: 58px 24px;
}

.stories-founder-band {
  padding-top: 40px;
}

.stories-founder-band-content {
  border-radius: 14px;
  border: 1px solid #e4dac9;
  background: #fffcf6;
  padding: 30px;
  max-width: 980px;
}

.stories-founder-band-content h2 {
  margin: 0;
  font-family: 'Playfair Display', serif;
  font-size: clamp(1.7rem, 3vw, 2.4rem);
  line-height: 1.1;
}

.stories-founder-band-content p {
  margin: 12px 0 0;
  color: #4a4d66;
  line-height: 1.8;
}

.stories-inline-link {
  display: inline-block;
  margin-top: 12px;
  color: #775625;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 1px;
  text-transform: uppercase;
  text-decoration: underline;
  text-underline-offset: 4px;
}

.stories-headline-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 12px;
  margin-bottom: 20px;
}

.stories-headline-row h2,
.stories-layout-section h2,
.stories-timeline-section h2,
.stories-bottom-cta h2 {
  margin: 0;
  font-family: 'Playfair Display', serif;
  font-size: clamp(1.6rem, 3vw, 2.3rem);
  color: #17182d;
  line-height: 1.2;
}

.stories-headline-row p {
  margin: 0;
  font-size: 13px;
  color: #666b81;
}

.stories-card-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 14px;
}

.stories-card {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e9dfd0;
  background: #fff;
  text-decoration: none;
  color: inherit;
  transition: transform 0.24s ease, box-shadow 0.24s ease;
}

.stories-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 14px 34px rgba(25, 31, 48, 0.11);
}

.stories-card-image {
  height: 232px;
  background-size: cover;
  background-position: center;
}

.stories-card-meta {
  padding: 14px;
}

.stories-card-index {
  color: #b3843d;
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 1px;
}

.stories-card-meta h3 {
  margin: 8px 0 4px;
  font-size: 15px;
  color: #191b31;
}

.stories-card-meta p {
  margin: 0;
  color: #696d84;
  font-size: 12px;
  line-height: 1.4;
}

.stories-layout-section {
  padding-top: 12px;
}

.stories-layout-grid {
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 22px;
}

.stories-layout-grid article {
  border: 1px solid #e4dac9;
  border-radius: 14px;
  background: #fff;
  padding: 28px;
}

.stories-layout-grid article p {
  margin: 0 0 14px;
  color: #4a4e66;
  line-height: 1.8;
}

.stories-layout-grid ul {
  margin: 0;
  padding-left: 18px;
  color: #202338;
  line-height: 1.85;
}

.stories-layout-image {
  border-radius: 14px;
  border: 1px solid #e4dac9;
  min-height: 420px;
  background-size: cover;
  background-position: center;
}

.stories-timeline-section {
  text-align: center;
}

.stories-timeline {
  margin-top: 22px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.stories-timeline-card {
  text-align: left;
  border-radius: 12px;
  border: 1px solid #24304a;
  background: #10182b;
  color: #c9cfdf;
  padding: 16px;
}

.stories-timeline-card span {
  display: inline-block;
  margin-bottom: 8px;
  font-size: 11px;
  letter-spacing: 1px;
  font-weight: 700;
  color: #d5ae68;
}

.stories-timeline-card h3 {
  margin: 0 0 7px;
  font-size: 15px;
  color: #ffffff;
}

.stories-timeline-card p {
  margin: 0;
  line-height: 1.6;
  font-size: 13px;
}

.stories-bottom-cta {
  text-align: center;
  padding-top: 8px;
  padding-bottom: 80px;
}

.stories-bottom-cta p {
  max-width: 640px;
  margin: 12px auto 22px;
  color: #5d6178;
  line-height: 1.7;
}

.stories-bottom-actions {
  display: flex;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
}

.stories-bottom-cta .stories-btn-secondary {
  color: #1d2138;
  background: #ffffff;
  border: 1px solid #e0d4c1;
}

@media (max-width: 1180px) {
  .stories-card-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .stories-timeline {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 900px) {
  .stories-layout-grid {
    grid-template-columns: 1fr;
  }

  .stories-card-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .stories-headline-row {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (max-width: 640px) {
  .stories-featured,
  .stories-layout-section,
  .stories-timeline-section,
  .stories-bottom-cta,
  .stories-founder-band {
    padding-left: 16px;
    padding-right: 16px;
  }

  .stories-card-grid,
  .stories-timeline {
    grid-template-columns: 1fr;
  }

  .stories-hero {
    min-height: 68vh;
  }

  .stories-card-image {
    height: 260px;
  }
}
`;
